'use client';

import { getText } from '@/utils/text/text';
import MohegansunLogo from '@/assets/images/mohegansun-logo.svg';
import FanduelLogo from '@/assets/images/fanduel-logo.png';
import { StateSelectorRegion } from './state-selector-region';
import { CloseButton } from '../../subcomponents/close-button';

import type { Region } from '@/types/constants';
import './styles/@theme';
import { BRAND, REGIONS } from '@/utils/constants';
import { useEffect } from 'react';
import {
    getStateSelectorCtaClickedProps,
    getStateSelectorModalViewedProps
} from '@/tracking/amplitude/events/general/state-selector';
import { useStore } from '@/store/store';
import { getConfig } from '@/utils/config/config';
import { SvgLoader } from '@/app/elements/svg/loader';

type StateSelectorProps = {
    regions: Region[];
};

export const StateSelector = ({ regions }: StateSelectorProps) => {
    const { sendEvent } = useStore((state) => state.tracking);
    const config = getConfig();

    const selectStateText = getText({
        text: `common.selectState.${process.env.COUNTRY}`
    });

    const stateSelectorInfoText = getText({
        text: `components.regionSelector.info.${process.env.BRAND}.${process.env.COUNTRY}`
    });

    const stateSelectorNotFoundState = getText({
        text: `components.regionSelectorPopup.casinoNotFoundState.${process.env.COUNTRY}`
    });

    const stateSelectorNotInterestedDetailsText = getText({
        text: `components.regionSelectorPopup.casinoNotInterestedDetails.web.${process.env.COUNTRY}`
    });

    const getLogoSrc = () => {
        return process.env.BRAND === BRAND.FANDUEL ? FanduelLogo.src : MohegansunLogo.src;
    };

    useEffect(() => {
        const eventProps = getStateSelectorModalViewedProps();
        sendEvent([{ destination: 'amplitude', eventProps }]);
    }, []);

    const handleClickCta = () => {
        const eventProps = getStateSelectorCtaClickedProps();
        sendEvent([{ destination: 'amplitude', eventProps }]);
    };

    return (
        <div className="region-selector-popup">
            <div className="popup-header">
                <img src={getLogoSrc()} className="logo" alt="logo" />
                <CloseButton />
            </div>
            <div className="popup-content">
                <div className="popup-info">
                    <h2>{selectStateText}</h2>
                    <h3>{stateSelectorInfoText}</h3>
                </div>
                <ul className="regions">
                    {regions
                        .filter((region) => Object.keys(REGIONS).includes(region))
                        .map((region) => {
                            return (
                                <li key={region} className="region">
                                    <StateSelectorRegion region={region as Region}>
                                        <div className="row">
                                            <div>{getText({ text: `components.regionNames.${region}` })}</div>
                                            <SvgLoader iconName="arrow-right" width="17" height="17" />
                                        </div>
                                    </StateSelectorRegion>
                                </li>
                            );
                        })}
                </ul>
                <div className="popup-info">
                    <h3 className="not-found-state">
                        <a
                            href="https://www.fanduel.com/casino/states-where-online-casinos-are-legal"
                            onClick={handleClickCta}>
                            {stateSelectorNotFoundState}
                        </a>
                    </h3>
                </div>

                {config.otherCountries?.length > 0 && (
                    <>
                        <div className="popup-info external-section">
                            <h3>{stateSelectorNotInterestedDetailsText}</h3>
                        </div>
                        <ul className="regions">
                            {config.otherCountries.map((country) => (
                                <li key={country.countryCode} className="region">
                                    <a href={country.link}>
                                        <div className="row">
                                            <div>
                                                {getText({ text: `components.countries.${country.countryCode}` })}
                                            </div>
                                            <SvgLoader iconName="arrow-right" width="17" height="17" />
                                        </div>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
};
